import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Lock,
  Heart,
  Zap,
  FileText,
  Users,
  AlertTriangle,
  Menu,
  X,
  Download,
  Eye,
  Stethoscope,
  ChevronRight,
  Send,
  MessageSquare,
  Check,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/lib/supabase";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function Landing() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly"); // monthly | yearly

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    supabase
      .from("pricing_plans")
      .select("*")
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (data) setPlans(data);
      });
  }, []);

  return (
    <div className="min-h-screen bg-background font-body" data-testid="landing-page">
      {/* Nav */}
      <nav
        className={`fixed top-0 z-50 w-full backdrop-blur-xl transition-all duration-300 ${
          scrolled ? "border-b border-border/50 bg-background/95 shadow-soft" : "bg-transparent"
        }`}
      >
        <div className="container mx-auto flex h-16 items-center justify-between px-6">
          <Logo size="md" />
          <div className="hidden items-center gap-8 md:flex">
            {[
              ["Features", "#features"],
              ["Our Story", "#story"],
              ["Pricing", "#pricing"],
              ["FAQ", "#faq"],
            ].map(([label, href]) => (
              <a
                key={label}
                href={href}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                data-testid={`nav-link-${label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {label}
              </a>
            ))}
            <a
              href="#contact"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              data-testid="nav-link-contact"
            >
              Contact
            </a>
          </div>
          <div className="hidden items-center gap-3 md:flex">
            <Button variant="ghost" size="sm" onClick={() => navigate("/auth")} data-testid="nav-sign-in">
              Sign In
            </Button>
            <Button size="sm" onClick={() => navigate("/auth?mode=signup")} data-testid="nav-get-started">
              Get Started
            </Button>
          </div>
          <button
            className="rounded-xl p-2 md:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="nav-menu-toggle"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="border-t border-border bg-background px-6 py-4 md:hidden">
            <div className="flex flex-col gap-3">
              {["Features", "Our Story", "Pricing", "FAQ"].map((l) => (
                <a
                  key={l}
                  href={`#${l.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setMenuOpen(false)}
                  className="py-2 text-sm font-medium text-muted-foreground"
                >
                  {l}
                </a>
              ))}
              <div className="flex gap-3 pt-2">
                <Button variant="ghost" size="sm" className="flex-1" onClick={() => navigate("/auth")}>
                  Sign In
                </Button>
                <Button size="sm" className="flex-1" onClick={() => navigate("/auth?mode=signup")}>
                  Get Started
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden pt-16">
        <div className="bg-gradient-hero grain relative">
          <div className="container mx-auto px-6 py-16 md:py-24 lg:py-32">
            <div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-16">
              <div className="flex-1 text-center lg:text-left">
                <motion.span
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  custom={0}
                  className="mb-4 inline-block rounded-full border border-primary-foreground/20 bg-primary-foreground/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground/80"
                >
                  Your Family Health Hub
                </motion.span>
                <motion.h1
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  custom={1}
                  className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-primary-foreground sm:text-5xl md:text-6xl"
                >
                  Never lose track of <span className="text-accent">care again.</span>
                </motion.h1>
                <motion.p
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  custom={2}
                  className="mt-6 max-w-xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg md:text-xl"
                >
                  Track medications, vaccinations, growth, and appointments for every person in your
                  family — all in one private, organized place.
                </motion.p>
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  custom={3}
                  className="mt-8 flex flex-col items-center gap-4 sm:flex-row lg:justify-start"
                >
                  <Button
                    size="lg"
                    onClick={() => navigate("/auth?mode=signup")}
                    data-testid="hero-cta-primary"
                    className="bg-accent text-accent-foreground hover:bg-accent/90 px-7 py-6 text-base"
                  >
                    Start Free — No Card Needed
                    <ChevronRight className="ml-1 h-5 w-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => {
                      const el = document.getElementById("features");
                      el?.scrollIntoView({ behavior: "smooth" });
                    }}
                    data-testid="hero-cta-secondary"
                    className="border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 px-7 py-6 text-base"
                  >
                    See How It Works
                  </Button>
                </motion.div>
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  custom={4}
                  className="mt-8"
                >
                  <p className="font-heading text-sm font-bold text-primary-foreground sm:text-base">
                    Your family's health — fully protected.
                  </p>
                  <p className="mt-1 text-xs text-primary-foreground/70 sm:text-sm">
                    Privacy-first design keeps every record secure.
                  </p>
                </motion.div>
              </div>
              <motion.div
                className="w-full max-w-md flex-shrink-0 lg:max-w-lg"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.25 }}
              >
                <img
                  src="/brand/hero-illustration.png"
                  alt="Family managing health records together"
                  className="w-full drop-shadow-2xl animate-float"
                />
              </motion.div>
            </div>
          </div>
          <div className="relative h-16 md:h-24">
            <svg
              viewBox="0 0 1440 96"
              preserveAspectRatio="none"
              className="absolute bottom-0 w-full"
            >
              <path d="M0 96V0C240 64 480 96 720 96C960 96 1200 64 1440 0V96H0Z" fill="hsl(40,20%,97%)" />
            </svg>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-6 sm:grid-cols-3">
            {[
              { icon: Lock, title: "Private by Design", desc: "Every record stays securely tied to your account." },
              { icon: Shield, title: "Built for Trust", desc: "Standards-aligned protections for your family's data." },
              { icon: Heart, title: "Made for Caregivers", desc: "Designed for the people quietly managing it all." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="flex flex-col items-center rounded-2xl bg-card p-8 text-center shadow-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
              >
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-secondary">
                  <item.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-bold">{item.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-gradient-section py-20">
        <div className="container mx-auto px-6">
          <motion.div
            className="mx-auto mb-14 max-w-2xl text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={0}
          >
            <span className="text-xs font-semibold uppercase tracking-wider text-accent">
              The Command Center
            </span>
            <h2 className="mt-3 font-heading text-3xl font-extrabold sm:text-4xl">
              Everything your family's health needs.
            </h2>
            <p className="mt-4 text-base text-muted-foreground sm:text-lg">
              Whether it's your toddler's vaccinations, your parent's medications, or an upcoming
              specialist — everything lives here.
            </p>
          </motion.div>
          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2">
            {[
              { icon: FileText, title: "Health Records", desc: "Vaccinations, prescriptions, allergies, and visit notes — organized and searchable." },
              { icon: Stethoscope, title: "Medications", desc: "Track every dose, frequency, and instruction for every member you care for." },
              { icon: AlertTriangle, title: "Emergency Ready", desc: "Critical info — allergies, contacts, conditions — accessible in one tap." },
              { icon: Users, title: "Family Profiles", desc: "From toddlers to aging parents — one profile per loved one." },
              { icon: Zap, title: "Instant Access", desc: "Find any record in seconds. Built for the moments that matter most." },
              { icon: Eye, title: "Full Visibility", desc: "Activity log shows every change and access — full transparency." },
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                className="group flex gap-5 rounded-2xl bg-card p-6 shadow-soft transition-shadow duration-300 hover:shadow-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <feature.icon className="h-6 w-6 text-primary group-hover:text-primary-foreground" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold">{feature.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section id="story" className="py-20">
        <div className="container mx-auto px-6">
          <div className="mx-auto max-w-3xl rounded-3xl bg-card p-8 shadow-card sm:p-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">Our Story</span>
              <h2 className="mt-3 font-heading text-3xl font-extrabold">
                Built by Parents. Proven in Real Life.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
                GuardianNest was born from real-life moments when families needed answers — and didn't
                have them. From the ER to the dinner table, caregivers do their best, but everyone hits
                the same wall: scattered apps, paperwork, and memory.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
                We built a single, calm place where every detail lives — insurance, allergies,
                medications, history, contacts — so families have <strong className="text-foreground">clarity in the
                moments that matter most.</strong>
              </p>
              <p className="mt-6 font-heading text-lg font-semibold text-primary">
                Because being prepared shouldn't be complicated.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Waitlist strip */}
      <WaitlistStrip />

      {/* Pricing */}
      <section id="pricing" className="bg-gradient-section py-20">
        <div className="container mx-auto px-6">
          <motion.div
            className="mx-auto mb-10 max-w-2xl text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={0}
          >
            <h2 className="font-heading text-3xl font-extrabold sm:text-4xl">Simple, Transparent Pricing</h2>
            <p className="mt-4 text-base text-muted-foreground sm:text-lg">
              Start free. Upgrade as your family's needs grow.
            </p>

            {/* Billing toggle */}
            <div className="mt-6 inline-flex rounded-full border border-border bg-card p-1 shadow-soft" data-testid="pricing-toggle">
              {[
                { key: "monthly", label: "Monthly" },
                { key: "yearly", label: "Yearly", tag: "Save 17%" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setBilling(opt.key)}
                  data-testid={`pricing-toggle-${opt.key}`}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                    billing === opt.key
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {opt.label}
                  {opt.tag && (
                    <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                      billing === opt.key ? "bg-accent text-accent-foreground" : "bg-accent/20 text-accent-foreground"
                    }`}>
                      {opt.tag}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </motion.div>

          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3" data-testid="pricing-grid">
            {plans.length === 0
              ? [1, 2, 3].map((i) => (
                  <div key={i} className="h-96 animate-pulse rounded-2xl bg-card shadow-soft" />
                ))
              : plans.map((p, i) => {
                  const cents = billing === "yearly" ? p.price_yearly_cents : p.price_monthly_cents;
                  const isFree = cents === 0;
                  const displayPrice = isFree
                    ? "Free"
                    : `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
                  const periodLabel = isFree ? "" : billing === "yearly" ? "/yr" : "/mo";
                  return (
                    <motion.div
                      key={p.id}
                      className={`relative flex flex-col rounded-2xl bg-card p-8 shadow-soft ${
                        p.is_featured ? "ring-2 ring-primary shadow-card" : ""
                      }`}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={fadeUp}
                      custom={i}
                      data-testid={`pricing-card-${p.slug}`}
                    >
                      {p.is_featured && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-xs font-semibold text-primary-foreground">
                          Most Popular
                        </span>
                      )}
                      <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                      {p.tagline && (
                        <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
                      )}
                      <div className="mt-4">
                        <span className="font-heading text-4xl font-extrabold">{displayPrice}</span>
                        {periodLabel && (
                          <span className="ml-1 text-sm text-muted-foreground">{periodLabel}</span>
                        )}
                      </div>
                      {billing === "yearly" && !isFree && p.price_monthly_cents > 0 && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          ≈ ${((p.price_yearly_cents / 100) / 12).toFixed(2)}/mo billed annually
                        </p>
                      )}
                      <ul className="mt-6 flex-1 space-y-3">
                        {(p.features || []).map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <Button
                        variant={p.is_featured ? "default" : "outline"}
                        className="mt-8 w-full"
                        size="lg"
                        onClick={async () => {
                          if (isFree) {
                            navigate("/auth?mode=signup");
                            return;
                          }
                          // Paid plans → auth check → Stripe checkout
                          const { data: userData } = await supabase.auth.getUser();
                          if (!userData?.user) {
                            navigate(`/auth?mode=signup&next=/#pricing&plan=${p.slug}&cycle=${billing}`);
                            return;
                          }
                          try {
                            const resp = await fetch(
                              `${process.env.REACT_APP_BACKEND_URL}/api/checkout/session`,
                              {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  plan_slug: p.slug,
                                  billing_cycle: billing,
                                  origin_url: window.location.origin,
                                  user_email: userData.user.email,
                                  user_id: userData.user.id,
                                }),
                              }
                            );
                            const data = await resp.json();
                            if (data.url) window.location.href = data.url;
                          } catch (e) {
                            alert("Could not start checkout. Please try again.");
                          }
                        }}
                        data-testid={`pricing-cta-${p.slug}`}
                      >
                        {isFree ? "Start Free" : `Choose ${p.name}`}
                      </Button>
                    </motion.div>
                  );
                })}
          </div>
          <p className="mt-8 text-center text-xs text-muted-foreground">
            Cancel anytime. 30-day money-back guarantee on every paid plan.
          </p>
          <div className="mt-6 text-center">
            <a
              href="/pricing"
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
              data-testid="landing-pricing-see-full"
            >
              See full plan comparison →
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20">
        <div className="container mx-auto px-6">
          <motion.h2
            className="mb-12 text-center font-heading text-3xl font-extrabold"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={0}
          >
            Questions? We've Got Answers.
          </motion.h2>
          <div className="mx-auto max-w-3xl space-y-4">
            {[
              {
                q: "Is my family's data really private?",
                a: "Absolutely. Your data is tied to your account and protected behind authentication. We never sell your data.",
              },
              {
                q: "Can I track multiple family members?",
                a: "Yes — create a profile for every loved one and switch between them with one click.",
              },
              {
                q: "What can I track today?",
                a: "Medications, vaccinations, growth measurements, and appointments. More coming as you grow with us.",
              },
              {
                q: "How is this different from a health app?",
                a: "It's a calm, organized health hub for the whole family — not a fitness tracker or symptom checker.",
              },
            ].map((faq, i) => (
              <motion.details
                key={faq.q}
                className="group rounded-2xl bg-card p-6 shadow-soft"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
              >
                <summary className="flex cursor-pointer list-none items-center justify-between font-heading text-base font-bold">
                  {faq.q}
                  <span className="ml-4 text-2xl transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <ContactSection />

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="mx-auto max-w-3xl rounded-3xl bg-gradient-hero grain relative p-10 text-center sm:p-14">
            <h2 className="font-heading text-3xl font-extrabold text-primary-foreground sm:text-4xl">
              The people you care for deserve organized care.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-primary-foreground/80 sm:text-lg">
              Join GuardianNest and turn the chaos into clarity — in just a few minutes.
            </p>
            <Button
              size="lg"
              className="mt-8 bg-accent text-accent-foreground hover:bg-accent/90 px-8 py-6 text-base"
              onClick={() => navigate("/auth?mode=signup")}
              data-testid="footer-cta"
            >
              Start Free Today
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-10">
        <div className="container mx-auto flex flex-col items-center justify-between gap-6 px-6 md:flex-row">
          <Logo size="sm" />
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} GuardianNest. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-4 gap-y-2 text-sm text-muted-foreground sm:gap-6">
            <a href="/install" className="hover:text-foreground" data-testid="footer-install">Install app</a>
            <a href="/pricing" className="hover:text-foreground" data-testid="footer-pricing">Pricing</a>
            <a href="/security" className="hover:text-foreground" data-testid="footer-security">Security</a>
            <a href="/privacy" className="hover:text-foreground" data-testid="footer-privacy">Privacy</a>
            <a href="/terms" className="hover:text-foreground" data-testid="footer-terms">Terms</a>
            <a href="/contact" className="hover:text-foreground" data-testid="footer-contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============================================================================
// Waitlist strip
// ============================================================================
function WaitlistStrip() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | done | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("submitting");
    setError("");
    const { error: err } = await supabase.from("waitlist_signups").insert({
      email: email.trim().toLowerCase(),
      source: "emergent_landing",
      referrer: typeof document !== "undefined" ? document.referrer : null,
    });
    if (err) {
      setStatus("error");
      setError(err.message || "Something went wrong. Please try again.");
      return;
    }
    setStatus("done");
    setEmail("");
  };

  return (
    <section className="py-14" data-testid="waitlist-section">
      <div className="container mx-auto px-6">
        <div className="relative mx-auto flex max-w-3xl flex-col items-center gap-6 overflow-hidden rounded-3xl bg-card px-8 py-10 shadow-card sm:flex-row sm:items-center sm:justify-between sm:px-12">
          <div className="max-w-md text-center sm:text-left">
            <span className="text-xs font-semibold uppercase tracking-wider text-accent">
              Not ready to sign up?
            </span>
            <h3 className="mt-1 font-heading text-2xl font-extrabold sm:text-3xl">
              Get updates as we roll out new features.
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              One email a month. No spam. Ever.
            </p>
          </div>
          <form onSubmit={submit} className="flex w-full max-w-md flex-col gap-2 sm:flex-row" data-testid="waitlist-form">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={status === "submitting" || status === "done"}
              className="flex-1"
              data-testid="waitlist-email"
            />
            <Button type="submit" disabled={status === "submitting" || status === "done"} data-testid="waitlist-submit">
              {status === "done" ? (
                <span className="inline-flex items-center gap-1">
                  <Check className="h-4 w-4" /> You're in
                </span>
              ) : status === "submitting" ? (
                "Adding you…"
              ) : (
                <span className="inline-flex items-center gap-1">
                  Join waitlist <ChevronRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>
        </div>
        {error && (
          <p className="mx-auto mt-3 max-w-3xl text-center text-xs text-destructive" data-testid="waitlist-error">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// Contact form
// ============================================================================
function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    const { error: err } = await supabase.from("contact_submissions").insert({
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      subject: form.subject.trim() || null,
      message: form.message.trim(),
      source: "emergent_contact",
    });
    if (err) {
      setStatus("error");
      setError(err.message || "Could not send. Please try again.");
      return;
    }
    setStatus("done");
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <section id="contact" className="bg-gradient-section py-20" data-testid="contact-section">
      <div className="container mx-auto px-6">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <span className="text-xs font-semibold uppercase tracking-wider text-accent">
              Reach out
            </span>
            <h2 className="mt-3 font-heading text-3xl font-extrabold sm:text-4xl">
              Have a question? We'd love to help.
            </h2>
            <p className="mt-3 max-w-lg text-base text-muted-foreground sm:text-lg">
              Whether you're stuck, curious about a feature, or want to partner — send a note.
            </p>
          </div>

          {status === "done" ? (
            <div className="mt-10 rounded-3xl bg-card p-10 text-center shadow-card" data-testid="contact-success">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100">
                <Check className="h-7 w-7 text-emerald-700" />
              </div>
              <h3 className="mt-4 font-heading text-xl font-bold">Message sent!</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                We'll get back to you within one business day.
              </p>
              <Button
                variant="ghost"
                onClick={() => setStatus("idle")}
                className="mt-4"
                data-testid="contact-send-another"
              >
                Send another
              </Button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="mt-10 space-y-5 rounded-3xl bg-card p-8 shadow-card sm:p-10"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Name
                  </label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    className="mt-1.5"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Email
                  </label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    className="mt-1.5"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Subject
                </label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="How can we help?"
                  className="mt-1.5"
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Message
                </label>
                <Textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about it…"
                  className="mt-1.5"
                  data-testid="contact-message"
                />
              </div>
              {error && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="contact-error">
                  {error}
                </div>
              )}
              <div className="flex flex-col-reverse items-center justify-between gap-4 sm:flex-row">
                <p className="text-xs text-muted-foreground">
                  <MessageSquare className="mr-1 inline h-3 w-3" />
                  We usually reply within 1 business day.
                </p>
                <Button
                  type="submit"
                  size="lg"
                  disabled={status === "submitting"}
                  data-testid="contact-submit"
                >
                  <Send className="mr-1 h-4 w-4" />
                  {status === "submitting" ? "Sending…" : "Send message"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
